import { API_BASE_URL } from '../utils/apiConfig';
import { apiClient } from "../utils/apiClient";

export type CalendarEvent = {
    id: string;
    title: string;
    start: string;
    end?: string;
    allDay?: boolean;
    source: 'polaris' | 'team' | 'personal' | 'company' | 'recruiting';
    color?: string;
    description?: string | null;
    webViewLink?: string | null;
    googleMeetLink?: string | null;
    readOnly?: boolean;
    recruitingEventType?: string | null;
    ncaaDivision?: 'D1' | 'D2' | null;
};

export type CalendarEventsResponse = {
    events: CalendarEvent[];
    hasPersonalCalendar: boolean;
    hasCompanyCalendar: boolean;
};

export type CreateEventRequest = {
    title: string;
    description?: string;
    allDay: boolean;
    start: string;
    end: string;
};

export type UpdateEventRequest = CreateEventRequest;

export type RecruitingEventsFetchResult = {
    events: CalendarEvent[];
    /** True when the API indicates no calendar exists for this sport (e.g. HTTP 404). */
    unavailable: boolean;
};

class CalendarService {
    private getHeaders(): HeadersInit {
        return {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };
    }

    async fetchPersonalEvents(startISO: string, endISO: string): Promise<CalendarEvent[]> {
        try {
            const qs = new URLSearchParams({
                start: startISO,
                end: endISO,
                includeGoogle: 'true',
                includePolaris: 'false',
                includeTeam: 'false',
            });

            const res = await apiClient(`${API_BASE_URL}/api/calendar/view?${qs}`, {
                headers: this.getHeaders(),
                credentials: 'include',
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch personal events: HTTP ${res.status}`);
            }

            return await res.json();
        } catch (error) {
            throw error;
        }
    }

    async fetchPolarisAndTeamEvents(startISO: string, endISO: string): Promise<CalendarEvent[]> {
        const qs = new URLSearchParams({
            start: startISO,
            end: endISO,
            includePolaris: 'true',
            includeTeam: 'true',
            includePersonal: 'false',
        });

        const res = await apiClient(`${API_BASE_URL}/api/calendar/view?${qs}`, {
            headers: this.getHeaders(),
            credentials: 'include',
        });

        if (!res.ok) {
            throw new Error('Unable to load calendar tasks. Please try again.');
        }

        const data = await res.json();
        return Array.isArray(data) ? data : [];
    }

    async fetchRecruitingEvents(
        startISO: string,
        endISO: string,
        sportId?: number,
        division?: 'D1' | 'D2'
    ): Promise<RecruitingEventsFetchResult> {
        try {
            const qs = new URLSearchParams({
                start: startISO,
                end: endISO,
            });
            if (sportId != null) {
                qs.set('sportId', String(sportId));
            }
            if (division) {
                qs.set('division', division);
            }

            const res = await apiClient(`${API_BASE_URL}/api/calendar/recruiting-events?${qs}`, {
                headers: this.getHeaders(),
                credentials: 'include',
            });

            if (res.status === 404) {
                return { events: [], unavailable: true };
            }

            if (!res.ok) {
                throw new Error(`Failed to fetch recruiting events: HTTP ${res.status}`);
            }

            const events: CalendarEvent[] = await res.json();
            return {
                events: events.map((event) => ({
                    ...event,
                    source: 'recruiting' as const,
                    readOnly: true,
                })),
                unavailable: false,
            };
        } catch (error) {
            throw error;
        }
    }

    async fetchCompanyEvents(startISO: string, endISO: string): Promise<CalendarEvent[]> {
        try {
            const qs = new URLSearchParams({
                start: startISO,
                end: endISO,
            });

            const res = await apiClient(`${API_BASE_URL}/api/calendar/company-events?${qs}`, {
                headers: this.getHeaders(),
                credentials: 'include', // Send cookies automatically
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch company events: HTTP ${res.status}`);
            }

            const events: CalendarEvent[] = await res.json();
            // Mark all company events as read-only
            return events.map(event => ({
                ...event,
                source: 'company' as const,
                readOnly: true,
            }));
        } catch (error) {
            throw error;
        }
    }

    async fetchGoogleEventDetails(eventId: string): Promise<any> {
        try {
            const res = await apiClient(`${API_BASE_URL}/api/calendar/google/${eventId}`, {
                headers: this.getHeaders(),
                credentials: 'include', // Send cookies automatically
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch Google event details: HTTP ${res.status}`);
            }

            return await res.json();
        } catch (error) {
            throw error;
        }
    }

    async createPolarisEvent(event: CreateEventRequest): Promise<void> {
        try {
            const res = await apiClient(`${API_BASE_URL}/api/calendar/polaris`, {
                method: 'POST',
                headers: this.getHeaders(),
                credentials: 'include', // Send cookies automatically
                body: JSON.stringify(event),
            });

            if (!res.ok) {
                throw new Error(`Failed to create event: HTTP ${res.status}`);
            }
        } catch (error) {
            throw error;
        }
    }

    async updatePolarisEvent(eventId: number, event: UpdateEventRequest): Promise<void> {
        try {
            const res = await apiClient(`${API_BASE_URL}/api/calendar/polaris/${eventId}`, {
                method: 'PUT',
                headers: this.getHeaders(),
                credentials: 'include', // Send cookies automatically
                body: JSON.stringify(event),
            });

            if (!res.ok) {
                throw new Error(`Failed to update event: HTTP ${res.status}`);
            }
        } catch (error) {
            throw error;
        }
    }
}

export const calendarService = new CalendarService();

